import { useCallback, useMemo, useState } from 'react';
import type { ScheduledMeeting } from '../../../contexts';
import type { UseDeleteConfirmationParams } from '../types';

const useDeleteConfirmation = ({ deleteMeeting, deletingRoom }: UseDeleteConfirmationParams) => {
  const [meetingToDelete, setMeetingToDelete] = useState<ScheduledMeeting | null>(null);

  const isProcessing = useMemo(
    () => meetingToDelete !== null && deletingRoom === meetingToDelete.room,
    [deletingRoom, meetingToDelete],
  );

  const initiateDelete = useCallback((meeting: ScheduledMeeting) => {
    setMeetingToDelete(meeting);
  }, []);

  const cancelDelete = useCallback(() => {
    setMeetingToDelete(null);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!meetingToDelete) {
      return;
    }

    await deleteMeeting(meetingToDelete.room)
      .catch(() => {
        // Error is handled by the mutation hook (e.g., shows a toast).
        // We catch it here simply to prevent unhandled promise rejection warnings.
      })
      .finally(() => {
        setMeetingToDelete(null);
      });
  }, [deleteMeeting, meetingToDelete]);

  return {
    meetingToDelete,
    isProcessing,
    initiateDelete,
    confirmDelete,
    cancelDelete,
  };
};

export default useDeleteConfirmation;
